/**
 * EnemySprites.ts - Enemy sprite configurations
 * Maps enemy IDs from EnemyData.sol to sprite sheet positions
 */

export interface EnemySpriteConfig {
  name: string;
  frameIndex: number;
  width: number;
  height: number;
  scale?: number;
  palette?: string; // Color palette variation
  animated?: boolean;
  animFrames?: number[];
  animFrameTime?: number;
}

// Enemy sprite configurations matching EnemyData.sol enemy IDs
// Sprites are in enemies.png (32x32 base size)
export const ENEMY_SPRITES: Record<number, EnemySpriteConfig> = {
  // ========== Early Game (IDs 1-10) ==========
  1: { name: 'Goblin', frameIndex: 0, width: 32, height: 32 },
  2: { name: 'Wolf', frameIndex: 1, width: 32, height: 32 },
  3: { name: 'Zombie', frameIndex: 2, width: 32, height: 32 },
  4: { name: 'Skeleton', frameIndex: 3, width: 32, height: 32 },
  5: { name: 'Pirate', frameIndex: 4, width: 32, height: 32 },
  6: { name: 'Shark', frameIndex: 5, width: 32, height: 32 },
  7: { name: 'Spider', frameIndex: 6, width: 32, height: 32 },
  8: { name: 'Snake', frameIndex: 7, width: 32, height: 32 },
  9: { name: 'Scorpion', frameIndex: 8, width: 32, height: 32 },
  10: { name: 'Imp', frameIndex: 9, width: 32, height: 32 },

  // ========== Mid Game (IDs 11-25) ==========
  11: { name: 'Ogre', frameIndex: 10, width: 32, height: 32, scale: 1.2 },
  12: { name: 'Giant', frameIndex: 11, width: 32, height: 32, scale: 1.5 },
  13: { name: 'Troll', frameIndex: 12, width: 32, height: 32, scale: 1.2 },
  14: { name: 'Vampire', frameIndex: 13, width: 32, height: 32 },
  15: { name: 'Mummy', frameIndex: 14, width: 32, height: 32 },
  16: { name: 'Wraith', frameIndex: 15, width: 32, height: 32, animated: true, animFrames: [15, 16, 17], animFrameTime: 200 },
  17: { name: 'Specter', frameIndex: 18, width: 32, height: 32, animated: true, animFrames: [18, 19, 20], animFrameTime: 250 },
  18: { name: 'Mindflayer', frameIndex: 21, width: 32, height: 32 },
  19: { name: 'Manticore', frameIndex: 22, width: 32, height: 32, scale: 1.3 },
  20: { name: 'Chimera', frameIndex: 23, width: 32, height: 32, scale: 1.4 },
  21: { name: 'Medusa', frameIndex: 24, width: 32, height: 32 },
  22: { name: 'Cockatrice', frameIndex: 25, width: 32, height: 32 },
  23: { name: 'Gargoyle', frameIndex: 26, width: 32, height: 32 },
  24: { name: 'Golem', frameIndex: 27, width: 32, height: 32, scale: 1.5 },
  25: { name: 'Dragon Zombie', frameIndex: 28, width: 32, height: 32, scale: 1.6 },

  // ========== Late Game (IDs 26-40) ==========
  26: { name: 'Blue Dragon', frameIndex: 29, width: 48, height: 48, scale: 1.5 },
  27: { name: 'Red Dragon', frameIndex: 30, width: 48, height: 48, scale: 1.5 },
  28: { name: 'Green Dragon', frameIndex: 31, width: 48, height: 48, scale: 1.5 },
  29: { name: 'Tyrannosaur', frameIndex: 32, width: 48, height: 48, scale: 1.6 },
  30: { name: 'Iron Golem', frameIndex: 33, width: 32, height: 32, scale: 1.5 },
  31: { name: 'Death Knight', frameIndex: 34, width: 32, height: 32, scale: 1.2 },
  32: { name: 'Dark Wizard', frameIndex: 35, width: 32, height: 32 },
  33: { name: 'Warmech', frameIndex: 36, width: 48, height: 48, scale: 1.8, animated: true, animFrames: [36, 37], animFrameTime: 150 },
  34: { name: 'Phantom', frameIndex: 38, width: 32, height: 32, animated: true, animFrames: [38, 39, 40], animFrameTime: 300 },
  35: { name: 'Death Eye', frameIndex: 41, width: 32, height: 32, animated: true, animFrames: [41, 42], animFrameTime: 500 },
  36: { name: 'Evil Eye', frameIndex: 43, width: 32, height: 32 },
  37: { name: 'Sorcerer', frameIndex: 44, width: 32, height: 32 },
  38: { name: 'Demon', frameIndex: 45, width: 48, height: 48, scale: 1.4 },
  39: { name: 'Lich', frameIndex: 46, width: 48, height: 48, scale: 1.5, animated: true, animFrames: [46, 47, 48], animFrameTime: 200 },
  40: { name: 'Kary', frameIndex: 49, width: 48, height: 48, scale: 1.6 },

  // ========== Bosses (IDs 41-43) ==========
  41: { name: 'Kraken', frameIndex: 50, width: 64, height: 64, scale: 2.0, animated: true, animFrames: [50, 51, 52], animFrameTime: 300 },
  42: { name: 'Tiamat', frameIndex: 53, width: 64, height: 64, scale: 2.0, animated: true, animFrames: [53, 54, 55], animFrameTime: 250 },
  43: { name: 'Chaos', frameIndex: 56, width: 64, height: 64, scale: 2.5, animated: true, animFrames: [56, 57, 58, 59], animFrameTime: 200 },
};

// Fallback colors for when sprites aren't loaded (based on enemy type)
export const ENEMY_FALLBACK_COLORS: Record<string, string> = {
  'Goblin': '#00aa00',
  'Wolf': '#884444',
  'Zombie': '#668866',
  'Skeleton': '#cccccc',
  'Pirate': '#aa6600',
  'Shark': '#4466aa',
  'Spider': '#442244',
  'Snake': '#448844',
  'Scorpion': '#886600',
  'Imp': '#ff4400',
  'Ogre': '#886644',
  'Giant': '#aa8866',
  'Troll': '#668844',
  'Vampire': '#440044',
  'Mummy': '#aa9966',
  'Wraith': '#666688',
  'Specter': '#8888aa',
  'Mindflayer': '#664488',
  'Manticore': '#aa6644',
  'Chimera': '#aa4466',
  'Medusa': '#448844',
  'Cockatrice': '#888844',
  'Gargoyle': '#666666',
  'Golem': '#888888',
  'Dragon Zombie': '#446644',
  'Blue Dragon': '#4488ff',
  'Red Dragon': '#ff4400',
  'Green Dragon': '#44aa44',
  'Tyrannosaur': '#664422',
  'Iron Golem': '#aaaaaa',
  'Death Knight': '#222244',
  'Dark Wizard': '#442266',
  'Warmech': '#666688',
  'Phantom': '#8888aa',
  'Death Eye': '#aa0000',
  'Evil Eye': '#880000',
  'Sorcerer': '#6644aa',
  'Demon': '#aa2200',
  'Lich': '#4444aa',
  'Kary': '#ff6600',
  'Kraken': '#226688',
  'Tiamat': '#884422',
  'Chaos': '#880088',
};

// Get enemy sprite config by ID
export function getEnemySprite(enemyId: number): EnemySpriteConfig | null {
  return ENEMY_SPRITES[enemyId] ?? null;
}

// Get fallback color for enemy
export function getEnemyFallbackColor(enemyName: string): string {
  return ENEMY_FALLBACK_COLORS[enemyName] ?? '#ff0000';
}

// Get scaled dimensions for an enemy
export function getEnemyDimensions(enemyId: number): { width: number; height: number } {
  const config = ENEMY_SPRITES[enemyId];
  if (!config) return { width: 32, height: 32 };

  const scale = config.scale ?? 1;
  return {
    width: config.width * scale,
    height: config.height * scale,
  };
}

// Get current animation frame for animated enemies
export function getEnemyAnimFrame(enemyId: number, time: number): number {
  const config = ENEMY_SPRITES[enemyId];
  if (!config || !config.animated || !config.animFrames || config.animFrames.length === 0) {
    return config?.frameIndex ?? 0;
  }

  const frameTime = config.animFrameTime ?? 200;
  const frameIndex = Math.floor(time / frameTime) % config.animFrames.length;
  const frame = config.animFrames[frameIndex];
  return frame ?? config.frameIndex;
}
